import React, { useEffect, useMemo, useState } from "react";
import { NavLink } from "react-router-dom";
import { SectionHeader } from "../../components/SectionHeader/SectionHeader";
import home from "../../assets/media/background/home1 Large.jpeg";
import { ContactForm } from "../../components/ContactForm/ContactForm";
import { HashLink } from "react-router-hash-link";
import { listEvents, EventSummary } from "../../api/events";
import { getEventImage } from "../EventPage/eventImages";
import LoadingPanel from "../../components/LoadingPanel/LoadingPanel";
import ErrorAlert from "../../components/ErrorAlert/ErrorAlert";
import { getUserFacingErrorMessage } from "../../utils/userFacingError";
import {
  deriveRegistrationStatusLabel,
  formatEventDateRange,
  isEventListedAsUpcoming,
} from "../../utils/eventDates";

function getExcerpt(event: EventSummary): string {
  if (event.excerpt) return event.excerpt;
  const firstPara = event.description.split(/\n\s*\n/)[0] ?? "";
  return firstPara.length > 280
    ? `${firstPara.slice(0, 280).trim()}…`
    : firstPara;
}

const HomePage: React.FC = () => {
  const [events, setEvents] = useState<EventSummary[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    let cancelled = false;
    setIsLoading(true);
    listEvents({ limit: 100 })
      .then((data) => {
        if (!cancelled) setEvents(data);
      })
      .catch((err) => {
        if (!cancelled) {
          setError(
            getUserFacingErrorMessage(err, "Unable to load events."),
          );
        }
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const { upcoming, past } = useMemo(() => {
    const upcoming: EventSummary[] = [];
    const past: EventSummary[] = [];

    for (const ev of events) {
      if (isEventListedAsUpcoming(ev)) {
        upcoming.push(ev);
      } else {
        past.push(ev);
      }
    }
    upcoming.sort(
      (a, b) =>
        new Date(a.registrationClosesAt ?? a.startDate).getTime() -
        new Date(b.registrationClosesAt ?? b.startDate).getTime(),
    );
    past.sort(
      (a, b) =>
        new Date(b.registrationClosesAt ?? b.startDate).getTime() -
        new Date(a.registrationClosesAt ?? a.startDate).getTime(),
    );
    return { upcoming, past };
  }, [events]);

  if (isLoading) {
    return (
      <LoadingPanel
        fill
        title="Preparing your event catalogue"
        subtitle="Retrieving upcoming and past conferences."
      />
    );
  }

  return (
    <div className="eh-page mb-32 flex flex-col gap-16">
      {/* HERO */}
      <section className="flex flex-col lg:flex-row gap-10 items-center justify-between mt-6">
        <div className="flex-1">
          <p className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-3">
            Academic conferences
          </p>
          <h1 className="font-bold text-4xl text-slate-900 mb-4 leading-tight">
            Discover and register for leading research events
          </h1>
          <p className="text-slate-600 leading-relaxed max-w-lg">
            Find conference registration, pricing, and secure checkout in one
            place — built for organisers and delegates alike.
          </p>
          <HashLink smooth to="/#events" className="inline-block mt-10">
            <span className="eh-btn-primary">Browse events</span>
          </HashLink>
        </div>
        <img
          className="w-full lg:max-w-[48%] h-auto rounded-sm border border-slate-200 shadow-sm"
          src={home}
          alt="Conference audience"
        />
      </section>

      {/* CTA */}
      <section className="eh-card text-center">
        <h2 className="font-bold text-2xl text-slate-900 mb-3">
          Host with Events House
        </h2>
        <p className="text-slate-600 max-w-xl mx-auto">
          From registration workflows to payment collection, we help academic
          conferences deliver a polished delegate experience.
        </p>
        <HashLink smooth to="/#contact-us" className="inline-block mt-10">
          <span className="eh-btn-secondary">Contact our team</span>
        </HashLink>
      </section>

      {error && (
        <ErrorAlert className="text-center">{error}</ErrorAlert>
      )}

      {/* UPCOMING EVENTS */}
      <section id="events">
        <SectionHeader
          subtitle="Upcoming"
          header="Events open for registration"
          description="Explore conferences currently accepting registrations."
        />
        {upcoming.length === 0 && !error && (
          <p className="text-slate-500">No upcoming events at the moment.</p>
        )}
        <div className="flex flex-col gap-6">
          {upcoming.map((ev) => (
            <Event
              key={ev.id}
              date={formatEventDateRange(ev.startDate, ev.endDate)}
              title={ev.title}
              description={getExcerpt(ev)}
              link={`/${ev.slug}`}
              img={getEventImage(ev.slug, ev.coverImageUrl)}
              status={deriveRegistrationStatusLabel(ev)}
              register
            />
          ))}
        </div>
      </section>

      {/* PAST EVENTS */}
      <section id="past-events">
        <SectionHeader
          subtitle="Archive"
          header="Past events"
          description="A record of conferences previously hosted on Events House."
        />
        {past.length === 0 && !error && (
          <p className="text-slate-500">No past events to show yet.</p>
        )}
        <div className="flex flex-col gap-4">
          {past.map((ev) => (
            <Event
              key={ev.id}
              date={formatEventDateRange(ev.startDate, ev.endDate)}
              title={ev.title}
              description={getExcerpt(ev)}
              link={`/${ev.slug}`}
              register={false}
            />
          ))}
        </div>
      </section>

      {/* GET IN TOUCH */}
      <section id="contact-us">
        <SectionHeader
          subtitle="Support"
          header="Get in touch"
          description="Questions about registration, payments, or hosting an event? Our team is here to help."
        />
        <div className="eh-card">
          <ContactForm />
        </div>
      </section>
    </div>
  );
};

export default HomePage;

function Event({
  date,
  title,
  description,
  link,
  status = "",
  img,
  register = true,
}: {
  date: string;
  title: string;
  description: string;
  link: string;
  status?: string;
  img?: string;
  register?: boolean;
}) {
  return (
    <article className="eh-card flex flex-col sm:flex-row gap-6 sm:items-stretch hover:border-slate-300 transition-colors">
      {img && (
        <div className="h-36 w-full shrink-0 overflow-hidden rounded-sm border border-slate-200 sm:h-auto sm:w-48">
          <img
            className="h-full w-full object-cover"
            src={img}
            alt=""
          />
        </div>
      )}
      <div className="flex flex-1 min-w-0 flex-col sm:items-start">
        <p className="flex flex-wrap items-center gap-2 text-sm text-slate-500 mb-2">
          <span>{date}</span>
          {status && (
            <span className="bg-amber-50 text-amber-900 text-xs font-medium px-2.5 py-0.5 border border-amber-200">
              {status}
            </span>
          )}
        </p>
        <NavLink to={link} className="block group">
          <h3 className="font-bold text-xl text-slate-900 group-hover:text-slate-700">
            {title}
          </h3>
          <p className="mt-2 text-slate-600 text-sm leading-relaxed line-clamp-3">
            {description}
          </p>
        </NavLink>
        {register && (
          <NavLink
            to={link}
            className="eh-btn-secondary mt-8 w-full text-center text-sm sm:w-auto"
          >
            View event
          </NavLink>
        )}
      </div>
    </article>
  );
}
